/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

/*
 * This illustrates how to create a widget that makes use of a number of different field definition types.
 */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import { CheckoutReviewAndSubmitPageContext } from "@insite/content-library/Pages/CheckoutReviewAndSubmitPage";
import { CheckoutShippingPageContext } from "@insite/content-library/Pages/CheckoutShippingPage";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { addressInfoDisplayStyles } from "@monsma/App/ComponentExtensions/AddressInfoDisplayExtension";
import { xCheckoutState, xHeaderActions } from "@monsma/App/Store/Reducers";
import { addressWrapper } from "@monsma/App/WidgetExtensions/Addresses/SharedAddressesComponentsStylesExtension";
import {
    leftColSpace,
    rightColSpace,
} from "@monsma/App/WidgetExtensions/CheckoutReviewAndSubmit/CheckoutReviewAndSubmitPaymentDetailsExtension";
import { StepperContext } from "@monsma/App/Widgets/CheckoutStepper/StepperContext";
import Edit from "@monsma/XDL/Icons/Edit";
import * as React from "react";
import { useContext } from "react";
import { connect, DispatchProp } from "react-redux";
import { css } from "styled-components";

export interface CheckoutReviewStyles {
    wrapper?: InjectableCss;

    gridItemR?: GridItemProps;
    gridItemL?: GridItemProps;
    gridItemF?: GridItemProps;

    linkWrapper?: InjectableCss;
    editLink?: LinkPresentationProps;

    headingText?: TypographyPresentationProps;
    bodyText?: TypographyPresentationProps;

    labelBlock?: TypographyPresentationProps;
    labelText?: TypographyPresentationProps;
    valueText?: TypographyPresentationProps;

    carrierWrapper?: InjectableCss;
    paymentWrapper?: InjectableCss;

    addressDisplay?: AddressInfoDisplayStyles;

    deliveryDateWrapper?: InjectableCss;
}

export const styles: CheckoutReviewStyles = {
    wrapper: {
        css: css`
            position: relative;
            min-height: 100px;
            padding-bottom: 20px;
            border-bottom: 1px solid ${getColor("common.ui12")};
            margin-bottom: 30px;
            &:last-child {
                margin-bottom: 0;
            }
        `,
    },
    gridItemL: {
        width: [12, 12, 12, 6, 6],
        css: css`
            flex-direction: column;
            ${leftColSpace}
        `,
    },
    gridItemR: {
        width: [12, 12, 12, 6, 6],
        css: css`
            flex-direction: column;
            ${rightColSpace}
        `,
    },
    gridItemF: {
        width: [12, 12, 12, 12, 12],
        css: css`
            flex-direction: column;
        `,
    },
    linkWrapper: {
        css: css`
            text-align: right;
        `,
    },
    editLink: {
        icon: {
            iconProps: {
                src: Edit,
                size: 12,
                color: "common.ui5",
            },
            position: "left",
        },
    },

    headingText: {
        variant: "h5",
    },
    bodyText: {
        variant: "p",
        css: css`
            font-size: 13px;
        `,
    },

    labelBlock: {
        css: css`
            font-size: 14px;
            font-weight: bold;
            display: block;
        `,
    },

    labelText: {
        css: css`
            font-size: 13px;
            font-weight: bold;
            margin-right: 5px;
        `,
    },

    valueText: {
        css: css`
            font-size: 13px;
        `,
    },

    addressDisplay: {
        wrapper: {
            css: css`
                ${addressInfoDisplayStyles.wrapper?.css}
                ${addressWrapper.css}
                margin-bottom: 15px;
            `,
        },
    },

    carrierWrapper: {
        css: css`
            ${addressWrapper.css}
            font-weight: bold;
            font-size: 14px;
            flex: 0;
        `,
    },
    paymentWrapper: {
        css: css`
            ${addressWrapper.css}
            margin-bottom: 15px;
            flex: 0;
            &:empty {
                display: none;
            }
        `,
    },

    deliveryDateWrapper: {
        css: css`
            background: ${getColor("common.border")};
            border-radius: 4px;
            padding: 20px;
            width: 100%;
            margin-bottom: 15px;
        `,
    },
};

const mapStateToProps = (state: ApplicationState & xCheckoutState) => {
    const { cartId, requestedDeliveryDate, requestedPickupDate } = state.pages.checkoutReviewAndSubmit;
    const cart = cartId ? getCartState(state, cartId).value : getCurrentCartState(state).value;

    return {
        checkoutShippingPage: getPageLinkByPageType(state, "CheckoutShippingPage"),
        isPickUp: state.context.session.fulfillmentMethod === FulfillmentMethod.PickUp,

        cart,
        billTo: getBillToState(state, cart?.billToId)?.value,
        shipTo: getShipToState(state, cart?.shipToId)?.value,
        session: state.context.session,

        requestedDeliveryDate,
        requestedPickupDate,

        cardHolder: state.xCheckout.cardHolder,
        lastFour: state.xCheckout.lastFour,
        expDate: state.xCheckout.expDate,
        ccType: state.xCheckout.ccType,
        po: state.xCheckout.po,
        paymentMethodX: state.xCheckout.paymentMethodX,
        paymentProfile: state.xCheckout.paymentProfile,
        customerReference1: state.xCheckout.customerReference1,
        deliveryMessaging: state.xCheckout.deliveryMessaging,
        addressFieldDataView: getAddressFieldsDataView(state),
        state,
        customSettings: getSettingsCollection(state).customSettings as any,
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & DispatchProp<xHeaderActions>;

const CheckoutReview = ({
    fields,
    checkoutShippingPage,
    isPickUp,
    cart,
    billTo,
    shipTo,
    session,
    requestedDeliveryDate,
    requestedPickupDate,
    cardHolder,
    lastFour,
    expDate,
    ccType,
    po,
    paymentMethodX,
    paymentProfile,
    customerReference1,
    addressFieldDataView,
    state,
    customSettings,
    deliveryMessaging
}: Props) => {
    const { currentStep, maxStep, setCurrentStep } = useContext(StepperContext);

    return (
        <>
            <StyledWrapper {...styles.wrapper}>
                <GridContainer>
                    <GridItem {...styles.gridItemL}>
                        <Typography {...styles.headingText}>{translate("Billing Address")}</Typography>
                        {billTo && (
                            <AddressInfoDisplay
                                {...billTo}
                                state={billTo.state?.abbreviation}
                                country={billTo.country?.abbreviation}
                                extendedStyles={styles.addressDisplay}
                                {...({ addressFields: addressFieldDataView?.value?.billToAddressFields } as any)}
                            />
                        )}
                    </GridItem>
                    <GridItem {...styles.gridItemR}>
                        <Typography {...styles.headingText}>
                            {translate(isPickUp ? "Pick Up Location" : "Shipping Address")}
                        </Typography>
                        {!isPickUp && shipTo && (
                            <AddressInfoDisplay
                                {...shipTo}
                                state={shipTo.state?.abbreviation}
                                country={shipTo.country?.abbreviation}
                                extendedStyles={styles.addressDisplay}
                                {...({ addressFields: addressFieldDataView?.value?.shipToAddressFields } as any)}
                            />
                        )}
                        {isPickUp && session.pickUpWarehouse && (
                            <AddressInfoDisplay
                                {...session.pickUpWarehouse}
                                companyName={session.pickUpWarehouse.description || session.pickUpWarehouse.name}
                                extendedStyles={styles.addressDisplay}
                                {...({
                                    addressFields: addressFieldDataView?.value?.shipToAddressFields,
                                } as any)}
                            />
                        )}
                    </GridItem>
                    {cart?.notes && (
                        <GridItem {...styles.gridItemF}>
                            <Typography {...styles.headingText}>{translate("Order Notes")}</Typography>
                            <Typography {...styles.bodyText} as="p">
                                {cart?.notes}
                            </Typography>
                        </GridItem>
                    )}
                </GridContainer>
                <StyledWrapper {...styles.linkWrapper}>
                    <Link
                        href={checkoutShippingPage?.url}
                        {...styles.editLink}
                        data-test-selector="checkout_edit_addresses"
                    >
                        {translate("Edit Address Information")}
                    </Link>
                </StyledWrapper>
            </StyledWrapper>

            {!isPickUp && (
                <StyledWrapper {...styles.wrapper}>
                    <GridContainer>
                        <GridItem {...styles.gridItemL}>
                            <Typography {...styles.headingText}>{translate("Shipping Options")}</Typography>
                            <StyledWrapper {...styles?.carrierWrapper}>{cart?.carrier?.description}</StyledWrapper>
                        </GridItem>
                        <GridItem {...styles.gridItemR}>
                            <StyledWrapper {...styles?.deliveryDateWrapper}>
                                <Typography variant="h5">{translate("Delivery Date")}</Typography>
                                <Typography variant="p">{siteMessage("Custom_Delivery_Date_Message")}</Typography>
                            </StyledWrapper>
                        </GridItem>
                        {(deliveryMessaging || cart?.properties?.deliveryMessaging) && (
                            <GridItem {...styles.gridItemF}>
                                <Typography {...styles.headingText}>{translate("Shipping Notes")}</Typography>
                                <Typography {...styles.bodyText} as="p">
                                    {deliveryMessaging || cart?.properties?.deliveryMessaging}
                                </Typography>
                            </GridItem>
                        )}
                    </GridContainer>
                    <StyledWrapper {...styles.linkWrapper}>
                        <Link
                            onClick={() => setCurrentStep(2)}
                            {...styles.editLink}
                            data-test-selector="checkout_edit_shipping"
                        >
                            {translate("Edit Shipping Information")}
                        </Link>
                    </StyledWrapper>
                </StyledWrapper>
            )}

            <StyledWrapper {...styles.wrapper}>
                <Typography {...styles.headingText}>{translate("Payment Information")}</Typography>
                <GridContainer>
                    {paymentMethodX !== "PO" && (
                        <GridItem {...styles.gridItemL}>
                            <StyledWrapper {...styles?.paymentWrapper}>
                                {/* Paid with CC */}
                                {paymentMethodX !== "PO" && !paymentProfile && lastFour && expDate && (
                                    <>
                                        <Typography as="strong" {...styles.labelBlock}>
                                            {translate("Paid with Credit Card:")}
                                        </Typography>
                                        <StyledWrapper>
                                            <Typography {...styles.valueText} as="div">
                                                {cardHolder}
                                            </Typography>
                                            <Typography {...styles.valueText} as="div">
                                                {ccType}, ****{lastFour}
                                            </Typography>
                                            <Typography {...styles.valueText}>Exp. {expDate} </Typography>
                                        </StyledWrapper>
                                    </>
                                )}

                                {/* Paid with Profile */}
                                {paymentProfile && (
                                    <>
                                        <Typography as="strong" {...styles.labelBlock}>
                                            {translate("Paid with Profile:")}
                                        </Typography>
                                        <Typography {...styles.valueText}>{paymentProfile}</Typography>
                                    </>
                                )}
                            </StyledWrapper>
                        </GridItem>
                    )}
                    <GridItem {...(paymentMethodX !== "PO" ? styles.gridItemR : styles.gridItemL)}>
                        {(po || customerReference1) && (
                            <StyledWrapper {...styles?.paymentWrapper}>
                                {/* display PO content */}
                                {po && (
                                    <StyledWrapper>
                                        <Typography {...styles.labelText} as="strong">
                                            {translate("Purchase Order:")}
                                        </Typography>
                                        <Typography {...styles.valueText}>{po}</Typography>
                                    </StyledWrapper>
                                )}
                                {customerReference1 && (
                                    <StyledWrapper>
                                        <Typography {...styles.labelText} as="strong">
                                            {translate("Job Name:")}
                                        </Typography>
                                        <Typography {...styles.valueText}>{customerReference1}</Typography>
                                    </StyledWrapper>
                                )}
                            </StyledWrapper>
                        )}
                    </GridItem>
                </GridContainer>
                {paymentMethodX === "PO" && (
                    <Typography {...styles.bodyText}>{siteMessage("Custom_Paid_With_Terms")}</Typography>
                )}
                <StyledWrapper {...styles.linkWrapper}>
                    <Link
                        onClick={() => setCurrentStep(3)}
                        {...styles.editLink}
                        data-test-selector="checkout_edit_payment"
                    >
                        {translate("Edit Payment Information")}
                    </Link>
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

const definition: WidgetDefinition = {
    group: "Checkout - Shipping",
    allowedContexts: [CheckoutShippingPageContext, CheckoutReviewAndSubmitPageContext],
    displayName: "Checkout Review",
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(CheckoutReview),
    definition,
};

export default widgetModule;
